import { useState, useEffect, useContext } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { AuthContext } from "../context/AuthContext";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  
  // Move all useState hooks to the top
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  
  const uid = searchParams.get("uid");
  const token = searchParams.get("token");

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  // Helper function to format password error messages
  const formatPasswordError = (errorMessage) => {
    if (errorMessage.includes("password") || errorMessage.includes("Password")) {
      // Split the error message by periods and format as bullet points
      const errorParts = errorMessage.split('.').filter(part => part.trim());
      return (
        <div>
          <ul className="text-xs text-red-700 space-y-1">
            {errorParts.map((part, index) => (
              <li key={index}>• {part.trim()}</li>
            ))}
          </ul>
        </div>
      );
    }
    return <p className="text-sm text-red-800">{errorMessage}</p>;
  };

  useEffect(() => {
    if (!uid || !token) {
      setError("Invalid or expired reset link.");
    }
  }, [uid, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    try {
      await axiosInstance.post(`${process.env.REACT_APP_API_URL}/api/v1/auth/password-reset-confirm/`, {
        uid,
        token,
        password,
      });
      setSubmitted(true);
    } catch (err) {
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError("Password reset failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex flex-col items-center">
            <img
              src="/logo_assets/android-chrome-192x192.png"
              alt="ResidentPass Logo"
              className="h-16 sm:h-20 w-auto mb-3 sm:mb-4"
            />
            <h1 className="text-2xl sm:text-3xl font-bold text-customIndigo mb-1 sm:mb-2">ResidentPass</h1>
          </div>
        </div>

        <div className="mt-6 sm:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-6 sm:py-8 px-4 shadow-xl rounded-lg sm:px-10 text-center space-y-4 sm:space-y-6">
            <div className="rounded-full bg-green-100 p-2 sm:p-3 mx-auto w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
              <svg className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Password reset successful!</h2>
            <p className="text-sm sm:text-base text-gray-600">You can now log in with your new password.</p>

            <button
              onClick={() => navigate("/login")}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-customIndigo hover:bg-customIndigo-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-customIndigo transition-colors duration-200"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex flex-col items-center">
          <img
            src="/logo_assets/android-chrome-192x192.png"
            alt="ResidentPass Logo"
            className="h-16 sm:h-20 w-auto mb-3 sm:mb-4"
          />
          <h1 className="text-2xl sm:text-3xl font-bold text-customIndigo mb-1 sm:mb-2">ResidentPass</h1>
          <p className="text-gray-600 text-xs sm:text-sm">Reset your password</p>
        </div>
      </div>

      <div className="mt-6 sm:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-6 sm:py-8 px-4 shadow-xl rounded-lg sm:px-10">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  {formatPasswordError(error)}
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            <div>
              <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-700">
                New Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-customIndigo focus:border-customIndigo text-sm sm:text-base transition-colors duration-200"
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Password must be at least 8 characters long and contain uppercase, lowercase, and numeric characters.
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-xs sm:text-sm font-medium text-gray-700">
                Confirm New Password
              </label>
              <div className="mt-1">
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-customIndigo focus:border-customIndigo text-sm sm:text-base transition-colors duration-200"
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Re-enter your new password to confirm it matches.
              </p>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-customIndigo hover:bg-customIndigo-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-customIndigo disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {loading ? (
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : null}
                {loading ? "Resetting password..." : "Reset Password"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
